import {
  BodyText,
  Button,
  Callout,
  Separator,
  Tooltip,
  toast,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { useParams } from "@tanstack/react-router";
import { CirclePlus } from "lucide-react";
import { Fragment, useState } from "react";
import { SectionContent } from "../components/SectionContent";
import { SectionHeader } from "../components/SectionHeader";
import { FamilyMemberHistoryForm } from "./components/FamilyMemberHistoryForm";
import { FamilyMemberHistoryItem } from "./components/FamilyMemberHistoryItem";
import { FamilyMemberHistorySkeleton } from "./components/FamilyMemberHistorySkeleton";
import {
  useAddFamilyMemberHistory,
  useGetFamilyMemberHistory,
} from "./network";

export const FamilyMemberHistory = () => {
  const { patientId } = useParams({ strict: false });
  const [isAddingFamilyMemberHistory, setIsAddingFamilyMemberHistory] =
    useState(false);
  const {
    data,
    isLoading: isLoadingFamilyMemberHistory,
    isError,
    refetch,
  } = useGetFamilyMemberHistory({ patientId: patientId as string });
  const { mutate: addFamilyMemberHistory, isPending: isSubmitting } =
    useAddFamilyMemberHistory({
      patientId: patientId as string,
      onSuccess: () => {
        toast.success("Family history added successfully");
        setIsAddingFamilyMemberHistory(false);
      },
      onError: (error) => {
        toast.error(`Failed to add family history: ${error.message}`);
      },
    });

  const showSkeleton = isLoadingFamilyMemberHistory;
  const showError = !showSkeleton && (isError || !data);
  const showEmpty =
    !showSkeleton &&
    !showError &&
    !isAddingFamilyMemberHistory &&
    data &&
    data.familyMemberHistory.length === 0;
  const showFamilyMemberHistory =
    !showSkeleton && !showError && data && data.familyMemberHistory.length > 0;

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader
        title="Family History"
        actionButtons={[
          <Tooltip tooltipContentText="Add" side="bottom" key="add">
            <Button
              variant="icon"
              className="m-0 p-0"
              onClick={() => {
                trackEvent(TRACKERS.ADD_FAMILY_HISTORY);
                setIsAddingFamilyMemberHistory(true);
              }}
              aria-label="Add family history"
            >
              <CirclePlus className="stroke-link h-4 w-4" />
            </Button>
          </Tooltip>,
        ]}
      />
      <SectionContent>
        {isAddingFamilyMemberHistory && (
          <>
            <FamilyMemberHistoryForm
              onCancel={() => {
                trackEvent(TRACKERS.CANCEL_FAMILY_HISTORY); // Canceling add new family history
                setIsAddingFamilyMemberHistory(false);
              }}
              onSubmit={(data) => {
                trackEvent(TRACKERS.SAVE_FAMILY_HISTORY); // Saving new family history
                addFamilyMemberHistory({
                  patientId: patientId as string,
                  relationshipCode: data.relationshipCode,
                  conditionCodes: data.conditionCodes.map((condition) => ({
                    coding: [
                      {
                        code: condition.code,
                        display: condition.display,
                        system: condition.system,
                      },
                    ],
                    text: condition.display,
                  })),
                });
              }}
              isSubmitting={isSubmitting}
            />
            <Separator className="w-full" />
          </>
        )}
        {showSkeleton && <FamilyMemberHistorySkeleton />}
        {showError && (
          <div className="flex flex-col gap-2 py-2 w-full">
            <Callout
              className="w-full cursor-pointer"
              variant="danger"
              title="Unable to load family history!"
              description="Please click here to try again."
              onClick={() => refetch()}
            />
          </div>
        )}
        {showEmpty && (
          <div className="flex py-2 w-full">
            <BodyText size="sm" textColor="tertiary">
              No family history documented
            </BodyText>
          </div>
        )}
        {showFamilyMemberHistory &&
          data.familyMemberHistory.map((familyMemberHistory) => (
            <Fragment key={familyMemberHistory.id}>
              <FamilyMemberHistoryItem
                key={familyMemberHistory.id}
                familyMemberHistory={familyMemberHistory}
              />
              <Separator className="w-full" />
            </Fragment>
          ))}
      </SectionContent>
    </div>
  );
};
